import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    HostListener,
    inject,
    OnInit,
    signal,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { parallel as radashParallel } from 'radash';
import { firstValueFrom } from 'rxjs';

import { FieldInterventionBloc } from '@connect-services/client/blocs/field-intervention.bloc';
import { FieldInterventionEntitiesListBloc } from '@connect-services/client/blocs/field-intervention-entities-list.bloc';
import { FieldInterventionLayerListBloc } from '@connect-services/client/blocs/field-intervention-layer-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { AbstractPaginationComponent } from '@connect-services/client/components/abstract-pagination/abstract-pagination.component';
import { TitleComponent } from '@connect-services/client/components/field-intervention-title/title/title.component';
import { UpdatePlanningProgressModalComponent } from '@connect-services/client/components/modals/field-intervention/update-planning-progress-modal/update-planning-progress-modal.component';
import {
    FieldInterventionEntityAttributions,
    FieldInterventionPlanningModalComponent,
    SelectState,
} from '@connect-services/client/components/modals/field-intervention-planning-modal/field-intervention-planning-modal.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { ComponentCanDeactivate, DeactivateDialogConfig } from '@connect-services/client/guards/can-dismount.guard';
import { EntityEnum } from '@connect-services/client/models/panel.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { FieldInterventionEntitiesListComponent } from '@connect-services/client/pages/field-intervention/field-intervention-entities-list/field-intervention-entities-list.component';
import { FieldInterventionEntitiesListFooterComponent } from '@connect-services/client/pages/field-intervention/field-intervention-entities-list/field-intervention-entities-list-footer/field-intervention-entities-list-footer.component';
import {
    EntitiesTableByLayerService,
    EntitiesTableTypeEnum,
} from '@connect-services/client/services/entities-table-by-layer.service';
import { FieldInterventionService } from '@connect-services/client/services/field-intervention.service';
import { FieldInterventionEntitiesFilterService } from '@connect-services/client/services/field-intervention-entities-filter.service';
import { FieldInterventionMapModeService } from '@connect-services/client/services/field-intervention-map-mode.service';
import { FilterResolverService } from '@connect-services/client/services/filter-resolver.service';
import { HasPermissionsService } from '@connect-services/client/services/has-permissions.service';
import { MapMaskService } from '@connect-services/client/services/map/mask/map-mask.service';
import { MapFieldInterventionService } from '@connect-services/client/services/map-field-intervention.service';
import { PanelService } from '@connect-services/client/services/panel.service';
import { RouterService } from '@connect-services/client/services/router.service';
import { SelectZoneLayerService } from '@connect-services/client/services/select-zone-layer.service';
import { UpdateFieldInterventionService } from '@connect-services/client/services/update-field-intervention.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { PaginatorComponent } from '@connect-services/src/lib/components/paginator/paginator.component';
import { TabComponent, TabWithState } from '@connect-services/src/lib/components/tab/tab.component';
import {
    FieldInterventionEntitiesService,
    FieldInterventionEntity,
    FieldInterventionForm,
    PermissionAliasEnum,
    Person,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { TabContentDirective } from '@shared/client/directives/tab-content.directive';

type EntityAttributionState = {
    isCheckedInAtLeastOneDefaultEntity: boolean;
    isContainedInAllDefaultEntities: boolean;
    isContainedInAllEntities: boolean;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        FieldInterventionEntitiesListComponent,
        FieldInterventionEntitiesListFooterComponent,
        NoDataToDisplayComponent,
        RessourcesModule,
        TabComponent,
        TabContentDirective,
        TitleComponent,
    ],
    selector: 'app-field-intervention-entities-filtered-table',
    styleUrl: './field-intervention-entities-filtered-table.component.scss',
    templateUrl: './field-intervention-entities-filtered-table.component.html',
})
export class FieldInterventionEntitiesFilteredTableComponent
    extends AbstractPaginationComponent
    implements ComponentCanDeactivate, OnInit
{
    protected readonly defaultTableIndex = signal<number>(-1);
    protected readonly hasManageFieldInterventionPermission = signal<boolean>(false);
    protected readonly tabs = computed<TabWithState[]>(() =>
        this.entitiesTableByLayerService.layerNames().map((layerName: string) => ({ active: true, name: layerName })),
    );
    protected readonly updateMode = signal<boolean>(false);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly ButtonType = ButtonType;
    protected readonly entitiesTableByLayerService = inject(EntitiesTableByLayerService);
    protected readonly fieldInterventionBloc = inject(FieldInterventionBloc);
    protected readonly fieldInterventionLayerListBloc = inject(FieldInterventionLayerListBloc);
    protected readonly fieldInterventionService = inject(FieldInterventionService);
    protected readonly PermissionAliasEnum = PermissionAliasEnum;
    protected readonly RoutePathEnum = RoutePathEnum;
    protected readonly updateFieldInterventionService = inject(UpdateFieldInterventionService);

    private readonly fieldInterventionEntitiesFilterService = inject(FieldInterventionEntitiesFilterService);
    private readonly fieldInterventionEntitiesListBloc = inject(FieldInterventionEntitiesListBloc);
    private readonly fieldInterventionEntitiesService = inject(FieldInterventionEntitiesService);
    private readonly fieldInterventionMapModeService = inject(FieldInterventionMapModeService);
    private readonly filterResolverService = inject(FilterResolverService);
    private readonly hasPermissionService = inject(HasPermissionsService);
    private readonly mapFieldInterventionService = inject(MapFieldInterventionService);
    private readonly mapMaskService = inject(MapMaskService);
    private readonly matDialog = inject(MatDialog);
    private readonly panelService = inject(PanelService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);
    private readonly routerService = inject(RouterService);
    private readonly selectZoneLayerService = inject(SelectZoneLayerService);
    private readonly translocoService = inject(TranslocoService);

    public constructor(private route: ActivatedRoute) {
        super();
        this.destroyRef.onDestroy(() => {
            this.fieldInterventionMapModeService.leaveFieldInterventionMapMode();
            this.mapMaskService.clearMasks();

            this.entitiesTableByLayerService.resetSearch();
            this.fieldInterventionService.resetHasEntities();
            this.entitiesTableByLayerService.activeLayers.set([]);
            this.entitiesTableByLayerService.tableType.set(null);
            this.entitiesTableByLayerService.selectedEntities.set({});
            void this.selectZoneLayerService.removeLayers();
            this.fieldInterventionEntitiesFilterService.resetData();
            this.filterResolverService.reset();
            this.fieldInterventionEntitiesFilterService.filterByParams.set({});
            this.filterResolverService.currentFilterType.set(null);
            this.updateFieldInterventionService.resetProgress();
            this.updateFieldInterventionService.updatedEntities.set([]);

            this.mapFieldInterventionService.defaultStyleFilterByLayer.clear();
        });

        effect(() => {
            if (
                !this.fieldInterventionBloc.value() ||
                !this.fieldInterventionEntitiesListBloc.value() ||
                !this.fieldInterventionLayerListBloc.value()
            ) {
                return;
            }

            this.fieldInterventionService.hasEntities.set(
                !!this.fieldInterventionEntitiesListBloc.value()!.items?.length,
            );
        });

        effect(() => {
            const isUpdateMode = this.updateMode();

            this.entitiesTableByLayerService.isPlanningSelectionActive.set(isUpdateMode);

            this.panelService.currentPanel.set(
                isUpdateMode ? EntityEnum.ENTITIES_LIST_UPDATE : EntityEnum.ENTITIES_LIST,
            );
        });
    }

    @HostListener('window:beforeunload')
    public canDeactivate(): boolean {
        if (!this.updateMode()) {
            return true;
        }

        return !this.updateFieldInterventionService.updatedEntities().length;
    }

    public async changeTab(layerNameIndex: number): Promise<void> {
        const currentLayerName = this.entitiesTableByLayerService.layerNames()[layerNameIndex];
        this.entitiesTableByLayerService.resetPagination();

        this.entitiesTableByLayerService.setActiveLayerByName(currentLayerName);
        await this.entitiesTableByLayerService.fetchEntities();
    }

    public getDeactivateDialogConfig(): DeactivateDialogConfig {
        const description = this.translocoService.translate('unsavedEditsWillBeLost');
        const pendingEditCount = this.updateFieldInterventionService.updatedEntities().length;

        return {
            description: pendingEditCount ? `${description} (${pendingEditCount})` : description,
            title: this.translocoService.translate('areYouSureToLeavePlanningEdit'),
            type: ModalType.DANGER,
            width: '50vw',
        };
    }

    public async goToPreviousPage(): Promise<void> {
        if (this.updateMode()) {
            const canLeave = await this.canLeaveUpdateMode();
            if (!canLeave) {
                return;
            }
            this.updateMode.set(false);
            this.updateFieldInterventionService.updatedEntities.set([]);

            return;
        }
        this.entitiesTableByLayerService.resetData();

        const path = [
            RoutePathEnum.PROJECTS,
            this.projectBloc.value()?.id,
            RoutePathEnum.FIELD_INTERVENTIONS,
            this.fieldInterventionBloc.value()?.id,
            RoutePathEnum.FIELD_INTERVENTION_DASHBOARD,
        ];
        await this.router.navigate(path);
    }

    public async ngOnInit(): Promise<void> {
        this.fieldInterventionMapModeService.enterFieldInterventionEntitiesMode();

        this.entitiesTableByLayerService.tableType.set(EntitiesTableTypeEnum.FILTERED_TABLE);
        await this.entitiesTableByLayerService.setLayers();
        this.entitiesTableByLayerService.initSelectionObjects(this.entitiesTableByLayerService.layers());

        this.hasManageFieldInterventionPermission.set(
            this.hasPermissionService.checkPermissions([PermissionAliasEnum.MANAGE_FIELD_INTERVENTION]),
        );

        this.route.queryParams.subscribe((params) => {
            const selectedLayer: string | undefined = params['selectedLayer'];

            if (!selectedLayer || !this.entitiesTableByLayerService.layerNames()!.includes(selectedLayer)) {
                this.defaultTableIndex.set(0);

                return;
            }

            this.defaultTableIndex.set(
                this.entitiesTableByLayerService
                    .layerNames()!
                    .findIndex((layerName: string): boolean => layerName === selectedLayer),
            );
        });
    }

    public async openPlanningModal(): Promise<void> {
        const progressDialogRef = this.openProgressModal('fetchingEntities', 'waitFetchingEntities');
        const defaultEntities: FieldInterventionEntity[] = await this.fetchPlannedEntities().finally(() => {
            progressDialogRef.close();
            this.updateFieldInterventionService.resetProgress();
        });
        const entities: FieldInterventionEntity[] = this.getEntitiesWithUpdate(defaultEntities);

        const { formIds, peopleIds } = this.getPreselectIds(entities);

        const peopleState: Record<string, EntityAttributionState> = this.getEntityState(
            entities,
            defaultEntities,
            peopleIds,
            'peopleIds',
        );

        const formState: Record<string, EntityAttributionState> = this.getEntityState(
            entities,
            defaultEntities,
            formIds,
            'formIds',
        );

        this.matDialog.open(FieldInterventionPlanningModalComponent, {
            data: {
                hasLoader: false,
                numberOfEntities: entities.length,
                preselectedFormCheckState: this.getDefaultCheckState(formState),
                preselectedFormIds: formIds,
                preselectedPeopleCheckState: this.getDefaultCheckState(peopleState),
                preselectedPeopleIds: peopleIds,
                startPlanning: async (attributions: FieldInterventionEntityAttributions): Promise<void> => {
                    await this.onPlanningModalClose(attributions, entities, defaultEntities);
                },
                updateMode: this.updateMode(),
            },
            width: '45rem',
        });
    }

    protected onPaginatorChange(paginator: PaginatorComponent): void {
        this.entitiesTableByLayerService.paginator = paginator;
    }

    protected async savePlanUpdate(): Promise<void> {
        const updatedEntities = this.updateFieldInterventionService.updatedEntities();
        const progressDialogRef = this.openProgressModal('updatePlanning', 'submit.savingChanges');

        this.updateFieldInterventionService.startProgress(updatedEntities.length);

        try {
            await radashParallel(5, updatedEntities, async (entity: FieldInterventionEntity) => {
                await firstValueFrom(
                    this.fieldInterventionEntitiesService.fieldInterventionEntityControllerPatch({
                        id: entity.id,
                        updateFieldInterventionEntityDto: {
                            forms: entity.formIds,
                            people: entity.peopleIds,
                            status: await this.getNewStatus(entity),
                        },
                    }),
                );

                this.updateFieldInterventionService.incrementProgress();
            });
        } finally {
            progressDialogRef.close();
            this.updateFieldInterventionService.resetProgress();
        }

        await this.entitiesTableByLayerService.setCurrentSourcesAndEntityType();
        this.entitiesTableByLayerService.manageHighlight();

        await this.entitiesTableByLayerService.fetchAllEntities(
            this.entitiesTableByLayerService
                .entities()
                .filter(
                    (entity: FieldInterventionEntity): boolean =>
                        !this.updateFieldInterventionService
                            .updatedEntities()
                            .find((updateEntity: FieldInterventionEntity): boolean => updateEntity.id === entity.id),
                )
                .map((entity: FieldInterventionEntity): string => entity.id),
        );

        this.updateFieldInterventionService.updatedEntities.set([]);
        this.updateMode.set(false);
    }

    private canLeaveUpdateMode(): boolean | Promise<boolean> {
        const hasPendingUpdates = !!this.updateFieldInterventionService.updatedEntities().length;
        if (!hasPendingUpdates) {
            return true;
        }

        return this.routerService.confirmBeforeLeavingPage(this.getDeactivateDialogConfig());
    }

    private entityHasBeenUpdated(
        defaultEntity: FieldInterventionEntity,
        updatedEntity: FieldInterventionEntity,
    ): boolean {
        const hasUpdateForm = !this.haveSameIds(defaultEntity.formIds ?? [], updatedEntity.formIds ?? []);
        const hasUpdatePeople = !this.haveSameIds(defaultEntity.peopleIds ?? [], updatedEntity.peopleIds ?? []);

        return hasUpdateForm || hasUpdatePeople;
    }

    private async fetchPlannedEntities(): Promise<FieldInterventionEntity[]> {
        const selectedEntities = await this.fetchSelectedEntities();

        this.updateFieldInterventionService.startProgress(selectedEntities.length);

        return radashParallel(5, selectedEntities, async (entity: FieldInterventionEntity) => {
            const hydratedEntity = await firstValueFrom(
                this.fieldInterventionEntitiesService.fieldInterventionEntityControllerGetOne({
                    id: entity.id,
                }),
            );

            this.updateFieldInterventionService.incrementProgress();

            return hydratedEntity;
        });
    }

    private async fetchSelectedEntities(): Promise<FieldInterventionEntity[]> {
        const manuallySelected: FieldInterventionEntity[] = Object.values(
            this.entitiesTableByLayerService.selectedEntities(),
        ).flatMap((currentValue: FieldInterventionEntity[]) => currentValue);

        let fullSelectedEntities: FieldInterventionEntity[] = [];
        if (this.entitiesTableByLayerService.fullSelectedLayers().length) {
            const excludedIds: string[] = Object.values(this.entitiesTableByLayerService.unselectedEntities()).flatMap(
                (unselectedList: FieldInterventionEntity[]) =>
                    unselectedList.map((unselectedEntity) => unselectedEntity.id),
            );

            fullSelectedEntities = await this.entitiesTableByLayerService.fetchAllEntities(
                excludedIds,
                this.entitiesTableByLayerService.fullSelectedLayers(),
            );
        }

        const selectedEntitiesMap = new Map<string, FieldInterventionEntity>();
        [...manuallySelected, ...fullSelectedEntities].forEach((entity: FieldInterventionEntity) => {
            selectedEntitiesMap.set(entity.id, entity);
        });

        return Array.from(selectedEntitiesMap.values());
    }

    private getDefaultCheckState(itemState: Record<string, EntityAttributionState>): {
        [key: string]: SelectState;
    } {
        return Object.entries(itemState).reduce(
            (
                acc: {
                    [key: string]: SelectState;
                },
                [key, itemStat],
            ) => {
                acc[key] = {
                    canBeUnchecked: !itemStat.isCheckedInAtLeastOneDefaultEntity,
                    checkState: itemStat.isContainedInAllEntities ? CheckboxState.CHECKED : CheckboxState.INDETERMINATE,
                    isEnabled: !itemStat.isContainedInAllDefaultEntities,
                };

                return acc;
            },
            {},
        );
    }

    private getEntitiesWithUpdate(defaultEntities: FieldInterventionEntity[]): FieldInterventionEntity[] {
        return [
            ...this.updateFieldInterventionService
                .updatedEntities()
                .filter(
                    (updatedEntity: FieldInterventionEntity) =>
                        !!defaultEntities.find((entity: FieldInterventionEntity) => updatedEntity.id === entity.id),
                ),
            ...defaultEntities.filter(
                (entity: FieldInterventionEntity): boolean =>
                    !this.updateFieldInterventionService
                        .updatedEntities()
                        .find((updateEntity: FieldInterventionEntity): boolean => updateEntity.id === entity.id),
            ),
        ];
    }

    private getEntityState(
        items: FieldInterventionEntity[],
        defaultItems: FieldInterventionEntity[],
        ids: string[],
        compareListKey: 'formIds' | 'peopleIds',
    ): Record<string, EntityAttributionState> {
        return Object.fromEntries(
            ids.map((id: string) => {
                const isContainsInAllEntities = items.every((item: FieldInterventionEntity) =>
                    item[compareListKey]?.includes(id),
                );

                const isCheckInAtLeastOneDefaultEntity = defaultItems.some((item: FieldInterventionEntity) =>
                    item[compareListKey]?.includes(id),
                );

                const isContainsInAllDefaultEntities = !isCheckInAtLeastOneDefaultEntity
                    ? false
                    : defaultItems.every((item: FieldInterventionEntity) => item[compareListKey]?.includes(id));

                return [
                    id,
                    {
                        isCheckedInAtLeastOneDefaultEntity: isCheckInAtLeastOneDefaultEntity,
                        isContainedInAllDefaultEntities: isContainsInAllDefaultEntities,
                        isContainedInAllEntities: isContainsInAllEntities,
                    },
                ];
            }),
        );
    }

    private async getNewStatus(
        fieldInterventionEntity: FieldInterventionEntity,
    ): Promise<FieldInterventionEntity.StatusEnum> {
        if (
            [FieldInterventionEntity.StatusEnum.PLANNED, FieldInterventionEntity.StatusEnum.ONGOING].includes(
                fieldInterventionEntity.status,
            )
        ) {
            return fieldInterventionEntity.status;
        }

        const savedEntity: FieldInterventionEntity = await firstValueFrom(
            this.fieldInterventionEntitiesService.fieldInterventionEntityControllerGetOne({
                id: fieldInterventionEntity.id,
            }),
        );

        if (savedEntity.formIds!.length === fieldInterventionEntity.formIds!.length) {
            return fieldInterventionEntity.status;
        }

        return FieldInterventionEntity.StatusEnum.ONGOING;
    }

    private getPreselectIds(entities: FieldInterventionEntity[]): { formIds: string[]; peopleIds: string[] } {
        return {
            formIds: [...new Set(entities.flatMap((entity: FieldInterventionEntity) => entity.formIds ?? []))],
            peopleIds: [...new Set(entities.flatMap((entity: FieldInterventionEntity) => entity.peopleIds ?? []))],
        };
    }

    private haveSameIds(firstIds: string[], secondIds: string[]): boolean {
        if (firstIds.length !== secondIds.length) {
            return false;
        }

        const secondIdsSet = new Set(secondIds);

        return firstIds.every((id: string) => secondIdsSet.has(id));
    }

    private async onPlanningModalClose(
        attributions: FieldInterventionEntityAttributions,
        entities: FieldInterventionEntity[],
        defaultEntities: FieldInterventionEntity[],
    ): Promise<void> {
        this.updateFieldInterventionService.updatedEntities.update(
            (updatedEntities: FieldInterventionEntity[]): FieldInterventionEntity[] => {
                entities.forEach((entity: FieldInterventionEntity) => {
                    const defaultEntity: FieldInterventionEntity | undefined = defaultEntities.find(
                        (item: FieldInterventionEntity) => item.id === entity.id,
                    );

                    const filtredAttribution: FieldInterventionEntityAttributions = {
                        ...attributions,
                        removedForms: attributions.removedForms.filter(
                            (removedForm: FieldInterventionForm) =>
                                !(defaultEntity?.formIds ?? []).includes(removedForm.id),
                        ),
                        removedPeople: attributions.removedPeople.filter(
                            (removedPerson: Person) => !(defaultEntity?.peopleIds ?? []).includes(removedPerson.id),
                        ),
                    };

                    const updatedEntity: FieldInterventionEntity = this.updateEntityFormAndPeople(
                        entity,
                        filtredAttribution,
                    );

                    updatedEntities = this.upsertById(updatedEntity, updatedEntities);
                });

                return updatedEntities.filter((updatedEntity: FieldInterventionEntity): boolean => {
                    const defaultEntity: FieldInterventionEntity | undefined = defaultEntities.find(
                        (entity: FieldInterventionEntity): boolean => entity.id === updatedEntity.id,
                    );
                    if (!defaultEntity) {
                        return true;
                    }

                    return this.entityHasBeenUpdated(defaultEntity, updatedEntity);
                });
            },
        );

        this.entitiesTableByLayerService.fullSelectedLayers.set([]);
        this.entitiesTableByLayerService.resetSelectionObjects(this.entitiesTableByLayerService.layers());
        await this.entitiesTableByLayerService.resetTableData();
    }

    private openProgressModal(
        titleKey: string,
        descriptionKey: string,
    ): MatDialogRef<UpdatePlanningProgressModalComponent> {
        return this.matDialog.open(UpdatePlanningProgressModalComponent, {
            autoFocus: false,
            data: {
                descriptionKey: descriptionKey,
                titleKey: titleKey,
            },
            disableClose: true,
            width: '40rem',
        });
    }

    private updateEntityFormAndPeople(
        entity: FieldInterventionEntity,
        attributions: FieldInterventionEntityAttributions,
    ): FieldInterventionEntity {
        const baseFormIds = new Set(entity.formIds ?? []);
        const basePeopleIds = new Set(entity.peopleIds ?? []);

        const removedFormIds = new Set((attributions.removedForms ?? []).map((form: FieldInterventionForm) => form.id));
        const removedPeopleIds = new Set((attributions.removedPeople ?? []).map((person: Person) => person.id));

        const nextFormIds = new Set<string>([
            ...baseFormIds,
            ...(attributions.forms ?? []).map((form: FieldInterventionForm) => form.id),
        ]);

        const nextPeopleIds = new Set<string>([
            ...basePeopleIds,
            ...(attributions.people ?? []).map((person: Person) => person.id),
        ]);

        const formsToRemove: string[] = [...removedFormIds].filter((id) => baseFormIds.has(id));
        const peopleToRemove: string[] = [...removedPeopleIds].filter((id) => basePeopleIds.has(id));

        formsToRemove.forEach((id: string) => nextFormIds.delete(id));
        peopleToRemove.forEach((id: string) => nextPeopleIds.delete(id));

        const forms = attributions.forms.filter((form: FieldInterventionForm) =>
            Array.from(nextFormIds).includes(form.id),
        );

        const people = attributions.people.filter((person: Person) => Array.from(nextPeopleIds).includes(person.id));

        return {
            ...entity,
            formIds: Array.from(nextFormIds),
            forms: forms,
            people: people,
            peopleIds: Array.from(nextPeopleIds),
        };
    }

    private upsertById<T extends { id: string }>(updatedData: T, array: T[]): T[] {
        const index = array.findIndex((data: T) => data.id === updatedData.id);

        if (index !== -1) {
            const tmp: T[] = [...array];
            tmp[index] = updatedData;

            return tmp;
        }

        return [...array, updatedData];
    }
}
